import React, { useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useLang, LANGS } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { ChevronDown, Menu, X, User } from "lucide-react";

const MAIN = [
  { to: "chefs", key: "nav.chefs" },
  { to: "experiences", key: "nav.experiences" },
  { to: "private-dining", key: "nav.privateDining" },
  { to: "journal", key: "nav.journal" },
  { to: "how-it-works", key: "nav.howItWorks" },
];
const MORE = [
  { to: "events", key: "nav.events" },
  { to: "corporate", key: "nav.corporate" },
  { to: "bespoke", key: "nav.bespoke" },
  { to: "gift-cards", key: "nav.giftCards" },
  { to: "become-a-chef", key: "nav.becomeChef" },
  { to: "about", key: "nav.about" },
  { to: "faq", key: "nav.faq" },
  { to: "contact", key: "nav.contact" },
];

export default function Nav() {
  const { t, lang } = useLang();
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const switchLang = (code) => {
    const rest = pathname.replace(/^\/(fr|en|ar|es|it|de|zh)/, "");
    setLangOpen(false);
    navigate(`/${code}${rest}`);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0A0A0A]/85 border-b border-white/10">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 h-[72px] flex items-center justify-between gap-4">
        <Link to={`/${lang}`} data-testid="nav-logo" className="shrink-0 flex items-center gap-2.5">
          <img src="/logo-crest.png" alt="Chef à Domicile" className="h-11 w-auto" />
          <span className="font-serif text-xl sm:text-2xl text-ivory">Chef <span className="text-gold italic">à</span> Domicile</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {MAIN.map((l) => (
            <Link key={l.to} to={`/${lang}/${l.to}`} data-testid={`nav-link-${l.to}`}
              className="gold-underline text-sm text-neutral-300 hover:text-ivory transition-colors">
              {t(l.key)}
            </Link>
          ))}
          <div className="relative">
            <button data-testid="nav-more-btn" onClick={() => setMoreOpen(!moreOpen)}
              className="flex items-center gap-1 text-sm text-neutral-300 hover:text-ivory transition-colors">
              {t("nav.more")} <ChevronDown size={14} />
            </button>
            {moreOpen && (
              <div className="absolute top-full mt-3 start-0 bg-coal border border-white/10 py-2 min-w-[200px] shadow-2xl" onMouseLeave={() => setMoreOpen(false)}>
                {MORE.map((l) => (
                  <Link key={l.to} to={`/${lang}/${l.to}`} data-testid={`nav-link-${l.to}`} onClick={() => setMoreOpen(false)}
                    className="block px-5 py-2.5 text-sm text-neutral-300 hover:text-gold hover:bg-white/5 transition-colors">
                    {t(l.key)}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className="flex items-center gap-3">
          <div className="relative">
            <button data-testid="language-selector" onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1.5 text-sm text-neutral-300 hover:text-gold border border-white/15 px-3 py-2 transition-colors uppercase">
              {lang} <ChevronDown size={13} />
            </button>
            {langOpen && (
              <div className="absolute top-full mt-2 end-0 bg-coal border border-white/10 py-1 min-w-[150px] shadow-2xl z-50">
                {LANGS.map((l) => (
                  <button key={l.code} data-testid={`lang-option-${l.code}`} onClick={() => switchLang(l.code)}
                    className={`block w-full text-start px-4 py-2 text-sm hover:bg-white/5 transition-colors ${l.code === lang ? "text-gold" : "text-neutral-300"}`}>
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {user ? (
            <div className="hidden sm:flex items-center gap-2">
              <Link to={`/${lang}/account`} data-testid="nav-account-link"
                className="flex items-center gap-2 text-sm text-neutral-300 hover:text-gold transition-colors px-2 py-2">
                <User size={15} /> {t("nav.account")}
              </Link>
              <button data-testid="nav-logout-btn" onClick={logout} className="text-xs text-neutral-500 hover:text-gold transition-colors">
                {t("nav.logout")}
              </button>
            </div>
          ) : (
            <Link to={`/${lang}/login`} data-testid="nav-login-link" className="hidden sm:block text-sm text-neutral-300 hover:text-gold transition-colors">
              {t("nav.login")}
            </Link>
          )}

          <Link to={`/${lang}/chefs`} data-testid="nav-find-chef-cta"
            className="hidden md:inline-flex bg-gold text-black text-sm font-semibold px-5 py-2.5 hover:bg-goldlight transition-colors">
            {t("nav.findChef")}
          </Link>

          <button data-testid="nav-mobile-toggle" className="lg:hidden text-ivory p-1" onClick={() => setOpen(!open)}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0A0A0A]/97 backdrop-blur-xl border-t border-white/10 max-h-[calc(100vh-72px)] overflow-y-auto">
          <div className="px-6 py-6 flex flex-col gap-1">
            {[...MAIN, ...MORE].map((l) => (
              <Link key={l.to} to={`/${lang}/${l.to}`} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.to}`}
                className="py-3 text-base text-neutral-200 border-b border-white/5 hover:text-gold transition-colors">
                {t(l.key)}
              </Link>
            ))}
            {user ? (
              <>
                <Link to={`/${lang}/account`} onClick={() => setOpen(false)} className="py-3 text-base text-gold">{t("nav.account")}</Link>
                <button onClick={() => { logout(); setOpen(false); }} className="py-3 text-start text-base text-neutral-400">{t("nav.logout")}</button>
              </>
            ) : (
              <Link to={`/${lang}/login`} onClick={() => setOpen(false)} data-testid="mobile-nav-login" className="py-3 text-base text-gold">{t("nav.login")}</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
